"use client";

/**
 * The reading — and writing — surface for "rename this spawn code everywhere
 * it is actually a reference, and never touch my files while you decide".
 *
 * `src/lib/gtaKnowledge/rename.ts` already does the whole judgement: `planRename`
 * decides which fields are in scope, refuses the batch outright on a collision,
 * and never writes a byte. What is left for this page is the part only a page
 * can get wrong, and it is the same short list every tool in this layer is held
 * to, sharpened for the one tool that writes bytes back out:
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. THE DRY RUN IS THE INTERFACE, NOT A STEP BEFORE IT
 *
 * There is no gesture on this page that reaches the download button without a
 * plan already on screen. `plan` is computed live, from whatever is in the two
 * text boxes right now, and every collision, every refused name and every code
 * that matched nothing is rendered *before* the button is even enabled — never
 * behind a "Preview" click of its own, because a preview nobody has to open is a
 * preview the competitor's own dialog already proved nobody reads. The button
 * is disabled outright while `plan.blocked` is true, and there is no second path
 * to the zip that skips that check: `handleDownload` reads the same `plan` this
 * screen already rendered, not a re-computed one that could disagree with it.
 *
 * 2. THE REFUSALS ARE THE PRODUCT, NOT AN ERROR STATE
 *
 * A collision, an invalid new name, or a code that matched nothing are rendered
 * with the same visual weight as the changes that will actually happen — the
 * competitor this replaces reported `Done.` while quietly overwriting a file on
 * exactly this input, so a refusal that a reader has to expand a disclosure to
 * see would be the same failure with a UI on top of it. `plan.collisions` goes
 * through `FindingsList`, `plan.invalid` and `plan.unreferenced` are their own
 * panels, and `plan.notes` — the audio banks and `.gxt2` files this pass could
 * not open — sits right next to the button that is about to run.
 *
 * 3. NOTHING UPLOADS, AND NOTHING ON DISK CHANGES UNDER THIS TOOL
 *
 * There is no `fetch`, `XMLHttpRequest` or `sendBeacon` of the user's own files
 * anywhere in this file's import graph — the only network call this page makes
 * is for our own shipped base-game name index, the same one `ClashFinderClient`
 * fetches, so a rename that would collide with a stock vehicle is caught before
 * the button is even live. And unlike the brief this tool was written from, this
 * page offers no "rename in place" mode at all: the only output is a zip built
 * fresh in memory and handed to the browser's own download, which the user
 * chooses to keep, discard or inspect. That is a deliberate narrowing for the
 * free, public version of this tool — see `buildRenameExport` below — and it is
 * also just a stricter version of the same safety property the plan already
 * has: a caller that never opens a write handle to the user's disk cannot lose
 * a race with itself the way the competitor's two-codes-swapped bug did.
 *
 * 4. THE ZIP IS BUILT FROM THE SAME PLAN THE SCREEN SHOWED, NOT A NEW ONE
 *
 * `buildRenameExport` takes the exact `RenamePlan` this component rendered and
 * the exact `RenameScan` its drop zone read — it recomputes nothing, and it
 * re-verifies every edit through `applyRenamePlan`, which is `patch.ts`'s
 * verified apply. Any edit that fails that check — which should never happen,
 * since nothing between "plan" and "download" gives the user a way to change
 * the files underneath the plan — aborts the whole download rather than
 * shipping a zip missing an edit while the screen still says it happened.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS
 *
 * This renders one scan and one batch of renames at a time. Picking again
 * replaces both, for the same reason `SpawnIndexClient.tsx` gives about its own
 * scan: `collectResources` only promises unique identities *within* one drop, so
 * unioning two picks is how two packs called `police` fold into one and a real
 * collision goes unreported.
 *
 * It renames what `rename.ts` renames and nothing more — see that module's own
 * LIMITS block for the full list of what it does not open. This page adds no
 * capability on top of the engine; it only has to not lose any of what the
 * engine already refuses to guess at.
 *
 * The tour is in manual mode and this tool has no sample pack, so the one thing
 * that opens it is the "Show me how this works" control in the intake row —
 * never a folder landing or a plan appearing. See `ShowTourButton` for why that
 * control is gated on nothing.
 */

import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Download, FolderOpen, Plus, Trash2, Upload } from "lucide-react";
import { FindingsList } from "@/components/gta/FindingsList";
import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  finishLabel as spawnRenameFinishLabel,
  steps as spawnRenameSteps,
} from "@/components/walkthrough/steps/spawnRename";
import { appendToDocumentBody } from "@/lib/dom";
import { collectResources, type CollectableGroup } from "@/lib/gtaKnowledge/collect";
import {
  fromArchives,
  fromDataTransfer,
  fromDirectoryPick,
  type DataTransferLike,
  type ResourceDrop,
} from "@/lib/gtaKnowledge/intake";
import { dryRunDiff } from "@/lib/gtaKnowledge/patch";
import {
  RENAME_TOOL,
  applyRenamePlan,
  planRename,
  type RenamePlan,
} from "@/lib/gtaKnowledge/rename";
import type { GtaResourceInput, GtaTextEdit, VanillaAssetNameIndex } from "@/lib/gtaKnowledge/types";
import { loadVanillaAssetNameIndex } from "@/lib/gtaKnowledge/vanillaAssetNames";
import { assertZipWritable, writeZip, type ZipEntry } from "@/lib/zipStream";

// ─── Small helpers ───────────────────────────────────────────────────────────

function n(value: number): string {
  return value.toLocaleString("en-US");
}

function count(value: number, singular: string, many?: string): string {
  return `${n(value)} ${value === 1 ? singular : (many ?? `${singular}s`)}`;
}

/**
 * A byte figure for the one line that prints one.
 *
 * Local for the reason `ClashFinderClient.tsx` and `SpawnIndexClient.tsx` both
 * give about their own copy: importing a shared formatter into a public page
 * costs every visitor the module it lives in, for nine lines of arithmetic.
 */
function formatSize(bytes: number): string {
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(2)} ${units[unit]}`;
}

/**
 * `identity/path` — the same join `rename.ts`'s private `qualifyPath` makes.
 *
 * That function is not exported, and for the reason `ClashFinderClient.tsx`
 * gives about its own `byteKey`: this page needs the *File handle* behind a
 * qualified path, which is a page concern (`patch.ts` and `rename.ts` never
 * touch a binary file), so a second, small, identically-argued copy lives here
 * rather than a shared export nothing else would use.
 */
function qualifyPath(identity: string, path: string): string {
  return `${identity}/${path}`;
}

function triggerDownload(href: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  appendToDocumentBody(anchor);
  anchor.click();
  anchor.remove();
}

// ─── Intake ──────────────────────────────────────────────────────────────────

/**
 * A drop's resources, with the attribution that keeps two packs two packs.
 *
 * Not imported from a sibling client — see `ClashFinderClient.tsx`'s own copy
 * of this function for the argument: importing one page's island to borrow nine
 * lines would put that whole island in this page's bundle.
 */
export function groupsOf(drop: ResourceDrop): CollectableGroup[] {
  return drop.resources.map((resource) => {
    const origin = drop.origins.get(resource.root);
    return {
      name: resource.name,
      files: resource.files,
      root: resource.root,
      ...(origin !== undefined && origin !== "" ? { origin } : {}),
    };
  });
}

/** What one drop produced, before any rename has been typed. */
export type RenameScan = {
  /** The resources, with their identities intact. The input to `planRename`. */
  inputs: GtaResourceInput[];
  /**
   * The `File` behind every dropped path, keyed by `qualifyPath(identity, path)`
   * so it lines up with `plan.fileRenames[].from` and `plan.edits[].file`.
   *
   * Held by the page and never by `rename.ts`, the same split `clash.ts` is
   * built on: `planRename` is pure and works from decoded text and a file
   * inventory, so a multi-megabyte `.yft` never reaches it. It is read back out
   * only once the user presses Download.
   */
  filesByPath: Map<string, File>;
  /** Everything intake and the collector could not read, in their own words. */
  notices: string[];
  /** The stock name index, when it loaded. Absent means that check did not run. */
  vanillaNames?: VanillaAssetNameIndex;
  /** Every path the drop held, including the ones no resource claimed. */
  files: number;
  /** Files the collector actually decoded as text, counted off what it returned. */
  opened: number;
  /** What the drop weighs, from the inventory rather than from anything read. */
  totalBytes: number;
};

/**
 * A `ResourceDrop`, read end to end.
 *
 * Exported so a test can drive intake and collection without a browser gesture,
 * and so the page has exactly one path from a drop to a scan whatever the
 * gesture was.
 */
export async function scanDrop(drop: ResourceDrop): Promise<RenameScan> {
  const { inputs, truncated } = await collectResources(groupsOf(drop));

  const filesByPath = new Map<string, File>();
  drop.resources.forEach((resource, index) => {
    // `collectResource` falls an empty root back to the resource's own name —
    // see `CollectableGroup.root` — and the qualified keys here have to agree
    // with `inputs[index].identity` exactly, or a file this page holds and a
    // path `rename.ts` names would silently refer to two different things.
    const identity = inputs[index]?.identity ?? resource.name;
    for (const entry of resource.files) {
      filesByPath.set(qualifyPath(identity, entry.path), entry.file);
    }
  });

  const notices = [...drop.truncated, ...truncated];

  // Fetched, not assumed — and said out loud when it fails, because a rename
  // that lands a custom vehicle on a stock name is a silent, permanent, in-game
  // replacement, and this is the one check standing between a typo and that.
  let vanillaNames: VanillaAssetNameIndex | undefined;
  try {
    vanillaNames = await loadVanillaAssetNameIndex();
  } catch (error) {
    notices.push(
      "The base-game name index did not load " +
        `(${error instanceof Error ? error.message : String(error)}), so a new name that collides ` +
        "with a base-game vehicle will not be caught here. Avoid short, generic names until it does.",
    );
  }

  return {
    inputs,
    filesByPath,
    notices: [...new Set(notices)],
    ...(vanillaNames ? { vanillaNames } : {}),
    files: drop.allPaths.length,
    opened: inputs.reduce((total, input) => total + input.texts.size, 0),
    totalBytes: drop.allPaths.reduce((total, entry) => total + (entry.size || 0), 0),
  };
}

// ─── Building the export ─────────────────────────────────────────────────────

/** The file this pass writes into the zip alongside the renamed pack itself. */
export const RENAME_MANIFEST_NAME = "_workshop-spawn-rename-manifest.txt";

/** Every edit, grouped by the qualified file it targets, in a stable order. */
function editsByFile(edits: readonly GtaTextEdit[]): Map<string, GtaTextEdit[]> {
  const map = new Map<string, GtaTextEdit[]>();
  for (const edit of edits) {
    const held = map.get(edit.file);
    if (held) held.push(edit);
    else map.set(edit.file, [edit]);
  }
  return map;
}

/** Every text this plan could touch, keyed the same way `plan.edits[].file` is. */
function textsByQualifiedPath(inputs: readonly GtaResourceInput[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const input of inputs) {
    const identity = input.identity ?? input.name;
    for (const [path, text] of input.texts) map.set(qualifyPath(identity, path), text);
  }
  return map;
}

/**
 * A per-file unified diff for every file this plan edits, sorted for a stable
 * screen and a stable manifest — the same `dryRunDiff` `patch.ts` built for
 * exactly this: a preview built from what `applyEdits` would actually do,
 * refusals included, not from the request.
 */
export function renameDiffs(
  plan: RenamePlan,
  texts: ReadonlyMap<string, string>,
): { file: string; diff: string }[] {
  return [...editsByFile(plan.edits).entries()]
    .map(([file, edits]) => ({ file, diff: dryRunDiff(texts.get(file) ?? "", edits) }))
    .sort((a, b) => a.file.localeCompare(b.file));
}

/** The change manifest written into the zip — every rename, in words, by hand. */
function buildChangeManifest(
  plan: RenamePlan,
  diffs: readonly { file: string; diff: string }[],
): string {
  const lines: string[] = [
    `${RENAME_TOOL} — change manifest`,
    `Generated ${new Date().toISOString()}`,
    "",
    `${count(plan.renamed.length, "spawn code")} renamed, ` +
      `${count(plan.fileRenames.length, "file")} moved, ` +
      `${count(plan.edits.length, "text edit")} across ${count(diffs.length, "file")}.`,
  ];

  if (plan.renamed.length > 0) {
    lines.push("", "Codes renamed:");
    for (const entry of plan.renamed) {
      lines.push(`  ${entry.code} -> ${entry.to}  (${entry.resource})`);
    }
  }
  if (plan.fileRenames.length > 0) {
    lines.push("", "Files moved:");
    for (const rename of plan.fileRenames) lines.push(`  ${rename.from} -> ${rename.to}`);
  }
  if (plan.unreferenced.length > 0) {
    lines.push("", "Requested, but matched nothing in this drop:");
    for (const code of plan.unreferenced) lines.push(`  - ${code}`);
  }
  if (plan.notes.length > 0) {
    lines.push("", "Notes:");
    for (const note of plan.notes) lines.push(`  - ${note}`);
  }
  if (diffs.length > 0) {
    lines.push(
      "",
      "Text changes, file by file. Every one of these is reversible by hand from this diff alone:",
    );
    for (const { file, diff } of diffs) lines.push("", `=== ${file} ===`, diff);
  }

  return lines.join("\n");
}

export type RenameExportResult = {
  entries: ZipEntry[];
  /** Non-empty only if a file changed under the plan between plan and download. */
  rejected: { edit: GtaTextEdit; reason: string }[];
};

/**
 * Turn a plan already on screen into the zip a download button hands to the
 * browser — never into a write against the folder the user dropped.
 *
 * Every original file the drop held is carried over: renamed where
 * `plan.fileRenames` says so, its text replaced where `applyRenamePlan` wrote
 * a patched version, and byte-identical to the original everywhere else. That
 * is the whole of decision #2 in `rename.ts`'s brief — "not re-serialising the
 * XML" — reaching the export: a file this plan does not touch is not read
 * through a parser at all here, it is streamed straight from the `File` handle
 * intake already held.
 *
 * `rejected` should always come back empty: `texts` is built from the very
 * `scan.inputs` the plan was computed from, and nothing on this page gives the
 * user a way to change those files between "plan" and "download". It is
 * checked anyway, because `applyRenamePlan` re-verifies for exactly the reason
 * `patch.ts`'s header gives — a promise that can be checked and refused beats
 * one that cannot — and a caller that ignored the result here would be the one
 * place in this whole layer that trusts a promise instead of checking it.
 */
export function buildRenameExport(scan: RenameScan, plan: RenamePlan): RenameExportResult {
  const pathRename = new Map(plan.fileRenames.map((rename) => [rename.from, rename.to]));
  const texts = textsByQualifiedPath(scan.inputs);
  const applied = applyRenamePlan(plan, texts);

  const entries: ZipEntry[] = [];
  for (const [qualified, file] of scan.filesByPath) {
    const outPath = pathRename.get(qualified) ?? qualified;
    const patchedText = applied.files.get(qualified);
    entries.push({ path: outPath, source: patchedText !== undefined ? patchedText : file });
  }

  const diffs = renameDiffs(plan, texts);
  entries.push({ path: RENAME_MANIFEST_NAME, source: buildChangeManifest(plan, diffs) });

  return { entries, rejected: applied.rejected };
}

/** What the zip is called. One resource keeps its own name; several are a pack. */
function exportFileName(scan: RenameScan): string {
  if (scan.inputs.length === 1) {
    return `${scan.inputs[0].name || "resource"}-renamed.zip`;
  }
  return "renamed-resources.zip";
}

// ─── The page ────────────────────────────────────────────────────────────────

type Phase =
  | { kind: "idle" }
  | { kind: "reading" }
  | { kind: "ready"; scan: RenameScan }
  | { kind: "failed"; message: string };

type RenameRow = { id: string; oldCode: string; newCode: string };

let rowSeq = 0;
function newRow(): RenameRow {
  rowSeq += 1;
  return { id: `row-${rowSeq}`, oldCode: "", newCode: "" };
}

type DownloadState =
  | { kind: "idle" }
  | { kind: "building" }
  | { kind: "done"; fileName: string; size: number }
  | { kind: "failed"; message: string };

export function SpawnRenameClient() {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [rows, setRows] = useState<RenameRow[]>(() => [newRow(), newRow()]);
  const [dragging, setDragging] = useState(false);
  const [download, setDownload] = useState<DownloadState>({ kind: "idle" });
  /** Raised by the replay button only. See the mount at the bottom of the component. */
  const [tourSignal, setTourSignal] = useState(0);

  const scan = phase.kind === "ready" ? phase.scan : null;

  const begin = useCallback(async (read: (signal: AbortSignal) => Promise<ResourceDrop>) => {
    const controller = new AbortController();
    setPhase({ kind: "reading" });
    setDownload({ kind: "idle" });
    try {
      const next = await scanDrop(await read(controller.signal));
      setPhase({ kind: "ready", scan: next });
    } catch (error) {
      setPhase({
        kind: "failed",
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  // ── The renames, as typed ─────────────────────────────────────────────────

  const renamesMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      const oldCode = row.oldCode.trim();
      if (oldCode === "") continue;
      // Last row wins on an exact duplicate spelling, same as a `Map` always
      // would — `planRename` normalizes case and reports its own refusal for
      // anything that still collides once it does.
      map.set(oldCode, row.newCode.trim());
    }
    return map;
  }, [rows]);

  // ── The plan — live, and the only thing the download button reads ────────

  const plan = useMemo<RenamePlan | null>(() => {
    if (!scan || renamesMap.size === 0) return null;
    return planRename(scan.inputs, renamesMap, scan.vanillaNames ? { vanillaNames: scan.vanillaNames } : {});
  }, [scan, renamesMap]);

  const texts = useMemo(
    () => (scan ? textsByQualifiedPath(scan.inputs) : new Map<string, string>()),
    [scan],
  );
  const diffs = useMemo(() => (plan ? renameDiffs(plan, texts) : []), [plan, texts]);

  const hasChanges = !!plan && (plan.edits.length > 0 || plan.fileRenames.length > 0);
  const canDownload = !!plan && !plan.blocked && hasChanges;

  // ── Gestures ──────────────────────────────────────────────────────────────

  const onFolder = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void begin((signal) => fromDirectoryPick(files, { signal }));
  };
  const onArchives = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void begin((signal) => fromArchives(Array.from(files), { signal }));
  };
  const onDrop = (dt: DataTransferLike | null) => {
    setDragging(false);
    if (!dt) return;
    void begin((signal) => fromDataTransfer(dt, { signal }));
  };

  const addRow = useCallback(() => setRows((current) => [...current, newRow()]), []);
  const removeRow = useCallback(
    (id: string) => setRows((current) => (current.length <= 1 ? current : current.filter((row) => row.id !== id))),
    [],
  );
  const updateRow = useCallback(
    (id: string, field: "oldCode" | "newCode", value: string) =>
      setRows((current) => current.map((row) => (row.id === id ? { ...row, [field]: value } : row))),
    [],
  );

  const handleDownload = useCallback(async () => {
    if (!scan || !plan || !canDownload) return;
    setDownload({ kind: "building" });
    try {
      const { entries, rejected } = buildRenameExport(scan, plan);
      if (rejected.length > 0) {
        // Should be unreachable — see `buildRenameExport`'s own comment — but
        // the house rule is that nothing may report success over an edit that
        // did not actually land, so a rejection here stops the download cold
        // rather than shipping a zip the screen's own plan disagrees with.
        throw new Error(
          `${count(rejected.length, "edit")} could not be verified against the files as read, so ` +
            `nothing was downloaded. ${rejected[0].reason} Re-drop the folder and try again.`,
        );
      }
      // An estimate, not a re-measure of every entry: the entries are almost
      // entirely the original files carried through unchanged, so the drop's
      // own inventoried size is the right order of magnitude for the refusal
      // message this exists to produce before a multi-gigabyte pack is asked
      // to allocate anything.
      assertZipWritable({ entries: entries.length, bytes: scan.totalBytes });
      const fileName = exportFileName(scan);
      const written = await writeZip(entries, { target: { kind: "download" } });
      if (written.url) {
        triggerDownload(written.url, fileName);
        setDownload({ kind: "done", fileName, size: written.size });
      } else {
        setDownload({ kind: "failed", message: "The zip was built but produced no downloadable file." });
      }
    } catch (error) {
      setDownload({
        kind: "failed",
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, [scan, plan, canDownload]);

  return (
    <div className="space-y-8">
      {/* ── Intake ──────────────────────────────────────────────────────── */}
      <section
        data-testid="spawn-rename-drop"
        data-walkthrough="srn-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          onDrop(event.dataTransfer as unknown as DataTransferLike);
        }}
        className={`not-prose rounded-2xl border-2 border-dashed p-5 transition-colors sm:p-6 ${
          dragging ? "border-primary/60 bg-primary/5" : "border-border/70 bg-muted/30"
        }`}
      >
        <h2 className="text-base font-semibold text-foreground">
          Drop the resource, or the zip you want to rename
        </h2>
        {/*
          Three claims, because this is a tool that writes bytes even though it
          is free: what gets opened, what leaves the tab, and what happens on
          disk — which for this tool is deliberately nothing at all. That third
          claim may not be shortened away: it is the one difference between
          this page and the competitor tool the brief was written against.
        */}
        <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
          Only text files are opened, each up to 4 MB; binary assets are counted by name and size
          and carried into the download unread.{" "}
          <strong className="text-foreground">Nothing you drop is uploaded, and nothing on your
          disk is changed</strong> — the only output is a zip you choose to download.
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
            <FolderOpen className="h-4 w-4" />
            Pick a folder
            <input
              data-testid="spawn-rename-folder"
              type="file"
              multiple
              className="sr-only"
              onChange={(event) => onFolder(event.target.files)}
              {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40">
            <Upload className="h-4 w-4" />
            Pick zips
            <input
              data-testid="spawn-rename-archives"
              type="file"
              multiple
              accept=".zip,.rar,.7z"
              className="sr-only"
              onChange={(event) => onArchives(event.target.files)}
            />
          </label>
          {/* In the intake row and gated on nothing — see `ShowTourButton`.
              With no sample pack on this tool it is the only way in. */}
          <ShowTourButton
            testId="spawn-rename-show-tour"
            onClick={() => setTourSignal((signal) => signal + 1)}
          />
        </div>

        {phase.kind === "reading" && (
          <p className="mt-4 text-sm text-muted-foreground">Reading the folder…</p>
        )}
        {phase.kind === "failed" && (
          <p className="mt-4 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>That folder could not be read: {phase.message}. Nothing was scanned.</span>
          </p>
        )}
        {scan && (
          <p
            className="mt-4 text-xs leading-6 text-muted-foreground"
            data-testid="spawn-rename-scope"
            data-walkthrough="srn-scope"
          >
            {count(scan.files, "file")} seen, {formatSize(scan.totalBytes)} in total, across{" "}
            {count(scan.inputs.length, "resource")}. {count(scan.opened, "of them was", "of them were")}{" "}
            opened and decoded as text; the rest were listed by name and size and carried through
            unread.
          </p>
        )}
        {scan && scan.notices.length > 0 && (
          <ul className="mt-3 space-y-1">
            {scan.notices.map((notice) => (
              <li key={notice} className="flex gap-2 text-xs leading-6 text-muted-foreground">
                <AlertTriangle className="mt-1 h-3 w-3 shrink-0 text-warning" />
                <span>{notice}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {scan && (
        <>
          {/* ── The renames, as typed ────────────────────────────────────── */}
          <section
            aria-label="Renames"
            data-testid="spawn-rename-rows"
            data-walkthrough="srn-rows"
            className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5"
          >
            <h2 className="text-base font-semibold text-foreground">Which codes to rename</h2>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
              One row per spawn code. Every field this pack anchors on that code —{" "}
              <code className="font-mono text-foreground">modelName</code>,{" "}
              <code className="font-mono text-foreground">txdName</code>,{" "}
              <code className="font-mono text-foreground">handlingId</code> and its{" "}
              <code className="font-mono text-foreground">handling.meta</code> entry, the
              streamed files, and more — is updated together. Nothing runs until you download.
            </p>

            <div className="mt-4 space-y-2">
              {rows.map((row) => (
                <div key={row.id} className="flex flex-wrap items-center gap-2">
                  <input
                    data-testid="spawn-rename-old"
                    value={row.oldCode}
                    onChange={(event) => updateRow(row.id, "oldCode", event.target.value)}
                    placeholder="Old spawn code, e.g. 20ambocr"
                    spellCheck={false}
                    className="w-56 max-w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-foreground outline-none transition-colors focus:border-primary/50"
                  />
                  <span className="text-xs text-muted-foreground">→</span>
                  <input
                    data-testid="spawn-rename-new"
                    value={row.newCode}
                    onChange={(event) => updateRow(row.id, "newCode", event.target.value)}
                    placeholder="New spawn code"
                    spellCheck={false}
                    className="w-56 max-w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-foreground outline-none transition-colors focus:border-primary/50"
                  />
                  <button
                    type="button"
                    data-testid="spawn-rename-remove-row"
                    disabled={rows.length <= 1}
                    onClick={() => removeRow(row.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive disabled:opacity-40"
                    aria-label="Remove this rename"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addRow}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
            >
              <Plus className="h-3.5 w-3.5" />
              Add another rename
            </button>
          </section>

          {/* ── The dry run — every plan reaches the screen before the button
              is ever live. ──────────────────────────────────────────────── */}
          {plan && (
            <section
              aria-label="What would happen"
              data-testid="spawn-rename-plan"
              data-walkthrough="srn-plan"
              className="space-y-4"
            >
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-foreground">
                  What would happen
                </h2>
                <p className="mt-1 max-w-3xl text-sm leading-7 text-muted-foreground">
                  Nothing below has been written anywhere. This is the complete plan — every file,
                  every edit and every refusal — for exactly the renames typed above.
                </p>
              </div>

              {plan.blocked && (
                <p
                  data-testid="spawn-rename-blocked"
                  className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Blocked. Nothing will be renamed or edited until every collision and invalid
                    name below is resolved.
                  </span>
                </p>
              )}

              {plan.warnings.length > 0 && (
                <FindingsList
                  findings={plan.warnings}
                  emptyLabel="No collision."
                  group="flat"
                />
              )}

              {plan.invalid.length > 0 && (
                <div
                  data-testid="spawn-rename-invalid"
                  className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4"
                >
                  <h3 className="text-sm font-semibold text-destructive">
                    {count(plan.invalid.length, "new name")} refused
                  </h3>
                  <ul className="mt-2 space-y-2">
                    {plan.invalid.map((issue) => (
                      <li key={`${issue.code}-${issue.to}`} className="text-xs leading-6 text-foreground">
                        <code className="font-mono">{issue.code}</code> →{" "}
                        <code className="font-mono">{issue.to}</code>: {issue.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {plan.unreferenced.length > 0 && (
                <div
                  data-testid="spawn-rename-unreferenced"
                  className="rounded-2xl border border-warning/40 bg-warning/5 p-4"
                >
                  <h3 className="text-sm font-semibold text-foreground">
                    {count(plan.unreferenced.length, "code")} matched nothing in this drop
                  </h3>
                  <p className="mt-1 text-xs leading-6 text-muted-foreground">
                    Not a vehicle, a handling entry, a carvariations row, a carcols kit or a
                    streamed file in any resource read here — check the spelling, or that you
                    dropped the right pack.
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {plan.unreferenced.map((code) => (
                      <li
                        key={code}
                        className="rounded-full border border-border bg-background px-2 py-0.5 font-mono text-xs text-foreground"
                      >
                        {code}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {plan.notes.length > 0 && (
                <div
                  data-testid="spawn-rename-notes"
                  className="rounded-2xl border border-border/60 bg-muted/30 p-4"
                >
                  <h3 className="text-sm font-semibold text-foreground">Notes</h3>
                  <ul className="mt-2 space-y-1.5">
                    {plan.notes.map((note) => (
                      <li key={note} className="text-xs leading-6 text-muted-foreground">
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!plan.blocked && !hasChanges && (
                <p className="text-sm text-muted-foreground">
                  Nothing to rename yet — every code above either is empty or matched nothing.
                </p>
              )}

              {!plan.blocked && hasChanges && (
                <>
                  {plan.renamed.length > 0 && (
                    <div
                      data-testid="spawn-rename-renamed"
                      className="overflow-x-auto rounded-2xl border border-border/60 bg-card"
                    >
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="px-4 py-2 font-medium">Code</th>
                            <th className="px-4 py-2 font-medium">Becomes</th>
                            <th className="px-4 py-2 font-medium">Resource</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.renamed.map((entry, index) => (
                            <tr
                              key={`${entry.identity}-${entry.code}-${index}`}
                              className="border-b border-border/40 last:border-0"
                            >
                              <td className="px-4 py-2 font-mono text-xs text-foreground">{entry.code}</td>
                              <td className="px-4 py-2 font-mono text-xs text-foreground">{entry.to}</td>
                              <td className="px-4 py-2 text-xs text-muted-foreground">{entry.resource}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {plan.fileRenames.length > 0 && (
                    <div
                      data-testid="spawn-rename-file-renames"
                      className="rounded-2xl border border-border/60 bg-card p-4"
                    >
                      <h3 className="text-sm font-semibold text-foreground">
                        {count(plan.fileRenames.length, "file")} would be renamed
                      </h3>
                      <ul className="mt-2 space-y-1 font-mono text-xs leading-6 text-muted-foreground">
                        {plan.fileRenames.map((rename) => (
                          <li key={`${rename.from}->${rename.to}`}>
                            {rename.from} <span className="text-foreground">→</span> {rename.to}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {diffs.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-foreground">
                        {count(diffs.length, "file")} would be edited
                      </h3>
                      {diffs.map(({ file, diff }) => (
                        <div key={file} data-testid="spawn-rename-diff">
                          <p className="mb-1 font-mono text-xs text-muted-foreground">{file}</p>
                          <pre className="overflow-x-auto rounded-xl border border-border/60 bg-muted/60 px-4 py-3 text-[11px] leading-5 text-foreground/90">
                            {diff}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── The download — the only path from this form to a byte ──── */}
              <div
                data-walkthrough="srn-download"
                className="flex flex-wrap items-center gap-3 rounded-2xl border border-border/60 bg-card p-4"
              >
                <button
                  type="button"
                  data-testid="spawn-rename-download"
                  disabled={!canDownload || download.kind === "building"}
                  onClick={() => void handleDownload()}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  {download.kind === "building"
                    ? "Building the zip…"
                    : `Download renamed pack (${count(plan.fileRenames.length, "file")} renamed, ` +
                      `${count(plan.edits.length, "edit")} across ${count(diffs.length, "file")})`}
                </button>
                {plan.blocked && (
                  <span className="text-xs text-destructive">
                    Fix the collisions and invalid names above first.
                  </span>
                )}
                {download.kind === "done" && (
                  <span
                    data-testid="spawn-rename-download-done"
                    className="text-xs text-muted-foreground"
                  >
                    Downloaded {download.fileName} ({formatSize(download.size)}).
                  </span>
                )}
                {download.kind === "failed" && (
                  <span
                    data-testid="spawn-rename-download-failed"
                    className="text-xs text-destructive"
                  >
                    {download.message}
                  </span>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* Manual mode, raised only by the replay button in the intake row. A
          tour that opened on a folder landing would throw a dimmer over the
          one moment a reader is deciding what to rename. */}
      <ToolWalkthrough
        steps={spawnRenameSteps}
        storageKey="workshop_tour_spawn_rename_v1"
        finishLabel={spawnRenameFinishLabel}
        openSignal={tourSignal}
      />
    </div>
  );
}
